import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faAddressBook } from '@fortawesome/pro-regular-svg-icons/faAddressBook';
import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { faUsers } from '@fortawesome/pro-regular-svg-icons/faUsers';
import { faWarehouse } from '@fortawesome/pro-regular-svg-icons/faWarehouse';
import { faArrowProgress } from '@fortawesome/pro-solid-svg-icons/faArrowProgress';
import { faBracketsCurly } from '@fortawesome/pro-solid-svg-icons/faBracketsCurly';
import { faBullseyeArrow } from '@fortawesome/pro-solid-svg-icons/faBullseyeArrow';
import { faChartSimple } from '@fortawesome/pro-solid-svg-icons/faChartSimple';
import { faRoute } from '@fortawesome/pro-solid-svg-icons/faRoute';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { t } from 'i18next';
import type { AccessMap, AccessNode, AccessReason, AssetType } from 'src/admin/models/AccessOverviewModel/graph';

// --- Roles aggregation ---

export type RoleSourceNodeType = 'user' | 'firm' | 'group';

export interface RoleSource {
    nodeId: string;
    nodeLabel: string;
    nodeType: RoleSourceNodeType;
    isDeny: boolean;
}

export interface AggregatedRole {
    role: string;
    sources: RoleSource[];
}

/** Walk all graph nodes and collect roles with their source attribution. */
export function aggregateRoles(graph: AccessMap): AggregatedRole[] {
    const roleMap = new Map<string, RoleSource[]>();

    for (const [, node] of graph) {
        let roles: Array<string | { deny: string }> = [];
        let nodeType: RoleSourceNodeType | null = null;

        if (node.type === 'user') {
            roles = node.meta.roles;
            nodeType = node.type;
        } else if (node.type === 'firm') {
            roles = node.meta.appliedRoles;
            nodeType = node.type;
        } else if (node.type === 'group') {
            roles = node.meta.roles;
            nodeType = node.type;
        }

        if (!nodeType) continue;

        for (const role of roles) {
            const isDeny = typeof role !== 'string';
            const roleName = isDeny ? role.deny : role;

            let sources = roleMap.get(roleName);
            if (!sources) {
                sources = [];
                roleMap.set(roleName, sources);
            }
            sources.push({
                nodeId: node.id,
                nodeLabel: node.label,
                nodeType,
                isDeny,
            });
        }
    }

    return [...roleMap.entries()]
        .map(([role, sources]) => ({ role, sources }))
        .sort((a, b) => a.role.localeCompare(b.role));
}

// --- Node icon helpers ---

export const nodeTypeIcon = (node: AccessNode): IconDefinition => {
    if (node.type === 'asset') return assetTypeIcon(node.meta.assetType);
    switch (node.type) {
        case 'user':
            return faUser;
        case 'firm':
            return faWarehouse;
        case 'group':
            return faUsers;
        case 'subscription':
            return faMoneyBill;
        case 'route':
            return faRoute;
        default: {
            const _exhaustive: never = node;
            return faUser;
        }
    }
};

export const assetTypeIcon = (assetType: AssetType): IconDefinition => {
    switch (assetType) {
        case 'account':
            return faAddressBook;
        case 'workspace':
            return faChartSimple;
        case 'idea':
            return faBullseyeArrow;
        case 'indicator':
            return faArrowProgress;
        case 'column-template':
            return faBracketsCurly;
        default: {
            const _exhaustive: never = assetType;
            return faBracketsCurly;
        }
    }
};

export const assetTypeLabel = (assetType: AssetType): string => {
    return t(`admin::access-overview::graph::node-type::${assetType}`);
};

// --- Graph view helpers ---

export type PermissionBadgeAssetType = 'workspace' | 'asset' | 'firm' | 'group' | 'account';

export const nodeColors: Record<string, string> = {
    user: 'border-blue-500 bg-blue-900/30',
    firm: 'border-amber-500 bg-amber-900/30',
    group: 'border-emerald-500 bg-emerald-900/30',
    subscription: 'border-purple-500 bg-purple-900/30',
    asset: 'border-cyan-500 bg-cyan-900/30',
    route: 'border-rose-500 bg-rose-900/30',
};

export const legendItems = [
    { label: () => t('admin::access-overview::graph::legend::user'), colorClass: 'bg-blue-500' },
    { label: () => t('admin::access-overview::graph::legend::firm'), colorClass: 'bg-amber-500' },
    { label: () => t('admin::access-overview::graph::legend::group'), colorClass: 'bg-emerald-500' },
    { label: () => t('admin::access-overview::graph::legend::subscription'), colorClass: 'bg-purple-500' },
    { label: () => t('admin::access-overview::graph::legend::asset'), colorClass: 'bg-cyan-500' },
    { label: () => t('admin::access-overview::graph::legend::route'), colorClass: 'bg-rose-500' },
];

export const mapNodeToAssetType = (node: AccessNode): PermissionBadgeAssetType => {
    if (node.type === 'firm') return 'firm';
    if (node.type === 'group') return 'group';
    if (node.type === 'asset' && node.meta.assetType === 'workspace') return 'workspace';
    if (node.type === 'asset' && node.meta.assetType === 'account') return 'account';
    return 'asset';
};

export const nodeTypeLabel = (node: AccessNode): string => {
    if (node.type === 'asset') {
        return t(`admin::access-overview::graph::node-type::${node.meta.assetType}`);
    }
    if (node.type === 'route') {
        return t('admin::access-overview::graph::node-type::route');
    }
    return t(`admin::access-overview::graph::node-type::${node.type}`);
};

export const edgeReasonLabel = (reason: AccessReason): string => {
    switch (reason.type) {
        case 'firm-membership':
            return t('admin::access-overview::graph::edge-reason::firm-membership', {
                permission: reason.permission ?? t('admin::access-overview::graph::none'),
            });
        case 'direct-group-membership':
            return t('admin::access-overview::graph::edge-reason::direct-group-membership', {
                permission: reason.permission ?? t('admin::access-overview::graph::none'),
            });
        case 'firm-context-group':
            return t('admin::access-overview::graph::edge-reason::firm-context-group', {
                permission: reason.permission ?? t('admin::access-overview::graph::none'),
            });
        case 'applied-group':
            return t('admin::access-overview::graph::edge-reason::applied-group', {
                permission: reason.permission ?? t('admin::access-overview::graph::none'),
            });
        case 'subscription':
            return t('admin::access-overview::graph::edge-reason::subscription');
        case 'asset-access':
            return t('admin::access-overview::graph::edge-reason::asset-access', {
                permission: reason.permission,
            });
        case 'route-access':
            return t('admin::access-overview::graph::edge-reason::route-access', {
                permission: reason.permission,
            });
        default: {
            const _exhaustive: never = reason;
            return String((_exhaustive as { type: string }).type);
        }
    }
};

// --- Source chip helpers ---

export const sourceTypeColors: Record<RoleSourceNodeType, string> = {
    user: 'border-blue-500/50 bg-blue-900/20 text-blue-400',
    firm: 'border-amber-500/50 bg-amber-900/20 text-amber-400',
    group: 'border-emerald-500/50 bg-emerald-900/20 text-emerald-400',
};

export const sourceTypeIcon = (type: RoleSourceNodeType): IconDefinition => {
    switch (type) {
        case 'user':
            return faUser;
        case 'firm':
            return faWarehouse;
        case 'group':
            return faUsers;
        default:
            return faUsers;
    }
};

// --- Detail panel helpers ---

export type DetailItem =
    | { kind: 'text'; label: string; value: string }
    | { kind: 'permission'; label: string; permission: string | null }
    | { kind: 'badges'; label: string; values: string[] };

type NodeDetailItemsMap = {
    [K in AccessNode['type']]: (node: Extract<AccessNode, { type: K }>) => DetailItem[];
};

export const nodeDetailItems: NodeDetailItemsMap = {
    user: (node) => [
        {
            kind: 'text',
            label: t('admin::access-overview::graph::detail-labels::name'),
            value: `${node.meta.firstName} ${node.meta.lastName}`,
        },
        { kind: 'text', label: t('admin::access-overview::graph::detail-labels::email'), value: node.meta.email },
        ...(node.meta.roles.length > 0
            ? [
                  {
                      kind: 'badges' as const,
                      label: t('admin::access-overview::graph::detail-labels::direct-roles'),
                      values: node.meta.roles.map((r) => (typeof r === 'string' ? r : r.deny)),
                  },
              ]
            : []),
    ],
    firm: (node) => [
        { kind: 'text', label: t('admin::access-overview::graph::detail-labels::firm-id'), value: node.meta.firmId },
        {
            kind: 'permission',
            label: t('admin::access-overview::graph::detail-labels::permission'),
            permission: node.meta.permission,
        },
        ...(node.meta.appliedRoles.length > 0
            ? [
                  {
                      kind: 'badges' as const,
                      label: t('admin::access-overview::graph::detail-labels::applied-roles'),
                      values: node.meta.appliedRoles.map((r) => (typeof r === 'string' ? r : r.deny)),
                  },
              ]
            : []),
        ...(node.meta.appliedGroupIds.length > 0
            ? [
                  {
                      kind: 'text' as const,
                      label: t('admin::access-overview::graph::detail-labels::applied-groups'),
                      value: t('admin::access-overview::graph::detail-labels::applied-groups-value', {
                          count: node.meta.appliedGroupIds.length,
                      }),
                  },
              ]
            : []),
    ],
    group: (node) => [
        { kind: 'text', label: t('admin::access-overview::graph::detail-labels::group-id'), value: node.meta.groupId },
        {
            kind: 'permission',
            label: t('admin::access-overview::graph::detail-labels::permission'),
            permission: node.meta.permission,
        },
        ...(node.meta.roles.length > 0
            ? [
                  {
                      kind: 'badges' as const,
                      label: t('admin::access-overview::graph::detail-labels::roles'),
                      values: node.meta.roles.map((r) => (typeof r === 'string' ? r : r.deny)),
                  },
              ]
            : []),
        ...(node.meta.firmId
            ? [
                  {
                      kind: 'text' as const,
                      label: t('admin::access-overview::graph::detail-labels::firm-context'),
                      value: node.meta.firmId,
                  },
              ]
            : []),
    ],
    subscription: (node) => [
        { kind: 'text', label: t('admin::access-overview::graph::detail-labels::plan'), value: node.meta.planName },
        { kind: 'text', label: t('admin::access-overview::graph::detail-labels::status'), value: node.meta.status },
        {
            kind: 'text',
            label: t('admin::access-overview::graph::detail-labels::subscription-id'),
            value: node.meta.subscriptionId,
        },
    ],
    asset: (node) => [
        {
            kind: 'text',
            label: t('admin::access-overview::graph::detail-labels::asset-type'),
            value: nodeTypeLabel(node),
        },
        { kind: 'text', label: t('admin::access-overview::graph::detail-labels::asset-id'), value: node.meta.assetId },
        {
            kind: 'permission',
            label: t('admin::access-overview::graph::detail-labels::permission'),
            permission: node.meta.permission,
        },
    ],
    route: (node) => [
        {
            kind: 'text',
            label: t('admin::access-overview::graph::detail-labels::route-id'),
            value: node.meta.routeId,
        },
        {
            kind: 'text',
            label: t('admin::access-overview::graph::detail-labels::description'),
            value: node.meta.description,
        },
        {
            kind: 'text',
            label: t('admin::access-overview::graph::detail-labels::firm-context'),
            value: node.meta.firm,
        },
        {
            kind: 'permission',
            label: t('admin::access-overview::graph::detail-labels::permission'),
            permission: node.meta.effectiveAccess,
        },
    ],
};
